import type { ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { useProposal, useProposalItems } from "@/hooks/use-api";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

export default function V2ProposalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useProposal(id ?? "");
  const proposal = (data as { proposal?: Row } | undefined)?.proposal;

  const { data: itemsData } = useProposalItems(id ?? "");
  const items = (itemsData?.items ?? []) as Row[];

  const proposalNumber = str(proposal?.proposal_number);
  const customerName = str(proposal?.customer_name);
  const status = str(proposal?.status) ?? "draft";
  const finalPrice = num(proposal?.final_price);
  const standardPrice = num(proposal?.standard_price);
  // ADV-1185: DB-computed totals from core_api.proposals_with_rep. Split
  // surfaces are shown only when there is recurring revenue.
  const oneTimeTotal = num(proposal?.one_time_total);
  const recurringTotal = num(proposal?.recurring_total);
  const hasRecurring = (recurringTotal ?? 0) > 0;
  const title = proposalNumber ? `Proposal ${proposalNumber}` : "Proposal";

  // ADV-1185: human-friendly frequency labels for recurring lines.
  const FREQUENCY_LABEL: Record<string, string> = {
    monthly: "monthly",
    quarterly: "quarterly",
    semiannual: "semiannual",
    annual: "annual",
  };

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !proposal}
      title={title}
      subtitle={customerName ?? undefined}
      breadcrumbs={[
        { label: "Proposals", href: "/proposals" },
        { label: title },
      ]}
      notFoundMessage={{
        title: "Proposal not found",
        description: "This proposal may have been deleted.",
      }}
      actions={<DetailPageActions entityLabel="Proposal" />}
    >
      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-5">
          {/* Inline properties strip */}
          <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
            <StatusBadge status={status} />
            {finalPrice != null && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span className="typo-mono font-medium">
                  {formatCurrency(finalPrice)}
                </span>
              </>
            )}
            {proposal?.created_at != null && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span>Created {formatDate(proposal.created_at as string)}</span>
              </>
            )}
          </div>

          {/* KPI row */}
          <KPIRow
            cards={[
              {
                label: "Total",
                value: finalPrice != null ? formatCurrency(finalPrice) : "—",
                subtitle:
                  standardPrice != null && standardPrice !== finalPrice
                    ? `List ${formatCurrency(standardPrice)}`
                    : undefined,
              },
              {
                label: "Line Items",
                value: String(items.length),
              },
              {
                label: "Status",
                value: status
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase()),
              },
              {
                label: "Created",
                value: proposal?.created_at
                  ? formatDate(proposal.created_at as string)
                  : "—",
              },
            ]}
          />

          {/* Scope — line items */}
          <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
            <div className="px-4 pt-3">
              <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                Scope
              </span>
            </div>
            <div className="mt-2">
              {items.length === 0 ? (
                <p className="px-4 py-3 typo-body text-[color:var(--color-text-lo)]">
                  No line items on this proposal.
                </p>
              ) : (
                items.map((item, i) => {
                  const qty = num(item.quantity);
                  const unitPrice = num(item.unit_price);
                  const lineTotal =
                    qty != null && unitPrice != null ? qty * unitPrice : null;
                  // ADV-1185: recurring lines display the plan's billing
                  // frequency beside the unit price and tag the line with
                  // a "Recurring" chip; one_time lines render unchanged.
                  const kind = str(item.kind);
                  const isRecurring = kind === "recurring";
                  const frequency = str(item.service_plan_billing_frequency);
                  const frequencyLabel = frequency
                    ? (FREQUENCY_LABEL[frequency] ?? frequency)
                    : null;
                  return (
                    <div
                      key={(item.id as string) ?? i}
                      className="flex items-baseline justify-between gap-3 px-4 py-3 border-t border-[color:var(--color-border)]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="typo-body font-medium text-[color:var(--color-text-hi)] truncate flex items-center gap-2">
                          <span className="truncate">
                            {str(item.description) ?? "Line item"}
                          </span>
                          {isRecurring && (
                            <span className="shrink-0 rounded-full border border-[color:var(--color-border)] px-2 py-0.5 typo-label text-[color:var(--color-text-md)] uppercase tracking-wide">
                              Recurring
                            </span>
                          )}
                        </div>
                        {(qty != null ||
                          unitPrice != null ||
                          frequencyLabel) && (
                          <div className="typo-label text-[color:var(--color-text-md)]">
                            {qty != null ? String(qty) : ""}
                            {unitPrice != null
                              ? ` @ ${formatCurrency(unitPrice)}`
                              : ""}
                            {isRecurring && frequencyLabel
                              ? ` · ${frequencyLabel}`
                              : ""}
                          </div>
                        )}
                      </div>
                      <div className="typo-mono font-medium text-[color:var(--color-text-hi)]">
                        {lineTotal != null
                          ? isRecurring && frequencyLabel
                            ? `${formatCurrency(lineTotal)}/${frequencyLabel}`
                            : formatCurrency(lineTotal)
                          : "—"}
                      </div>
                    </div>
                  );
                })
              )}
              {/* ADV-1185: split totals when there is recurring revenue. The
                  values come from the DB view (one_time_total + recurring_total)
                  — never sum line items in TS. */}
              {hasRecurring ? (
                <>
                  <div className="flex items-baseline justify-between px-4 py-3 border-t border-[color:var(--color-border)]">
                    <span className="typo-label text-[color:var(--color-text-md)] uppercase tracking-wide">
                      One-Time
                    </span>
                    <span className="typo-mono font-semibold text-[color:var(--color-text-hi)]">
                      {formatCurrency(oneTimeTotal ?? 0)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between px-4 py-3 border-t border-[color:var(--color-border)]">
                    <span className="typo-label text-[color:var(--color-text-md)] uppercase tracking-wide">
                      Recurring
                    </span>
                    <span className="typo-mono font-semibold text-[color:var(--color-text-hi)]">
                      {formatCurrency(recurringTotal ?? 0)}
                    </span>
                  </div>
                </>
              ) : (
                finalPrice != null && (
                  <div className="flex items-baseline justify-between px-4 py-3 border-t border-[color:var(--color-border)]">
                    <span className="typo-label text-[color:var(--color-text-md)] uppercase tracking-wide">
                      Total
                    </span>
                    <span className="typo-mono font-semibold text-[color:var(--color-text-hi)]">
                      {formatCurrency(finalPrice)}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Project notes */}
          {str(proposal?.project_notes) && (
            <RailSection title="Project Notes">
              <p className="typo-body text-[color:var(--color-text-md)] whitespace-pre-wrap">
                {proposal?.project_notes as string}
              </p>
            </RailSection>
          )}

          {/* Linked customer */}
          {customerName && proposal?.customer_role_id != null && (
            <button
              type="button"
              onClick={() =>
                navigate(`/customers/${proposal.customer_role_id as string}`)
              }
              className="flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-left hover:bg-[color:var(--color-elevated)] w-full sm:w-1/2"
            >
              <User className="h-4 w-4 shrink-0 text-[color:var(--color-text-md)]" />
              <span className="flex-1 min-w-0">
                <span className="block truncate typo-body font-medium text-[color:var(--color-text-hi)]">
                  {customerName}
                </span>
                <span className="block typo-label text-[color:var(--color-text-md)]">
                  Customer
                </span>
              </span>
            </button>
          )}

          {/* Activity timeline — full feed at bottom of main column (Pattern B/C,
              no tabs on this page yet). Right rail also carries the preview. */}
          {id && (
            <RailSection title="Activity">
              <EntityActivityFeed
                entityType="proposal"
                entityId={id}
                emptyDescription="All messages, calls, and notes for this proposal appear here once the first contact is logged."
              />
            </RailSection>
          )}
        </div>

        {/* Right rail */}
        <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
          <RailSection title="Properties">
            <InfoRow label="Status" value={status} />
            <InfoRow label="Customer" value={customerName} />
            <InfoRow label="Proposal #" value={proposalNumber} />
            <InfoRow label="Project Type" value={str(proposal?.project_type)} />
            <InfoRow
              label="Total"
              value={finalPrice != null ? formatCurrency(finalPrice) : null}
            />
            <InfoRow
              label="List Price"
              value={
                standardPrice != null ? formatCurrency(standardPrice) : null
              }
            />
            <InfoRow
              label="Submitted"
              value={
                proposal?.submit_date
                  ? formatDate(proposal.submit_date as string)
                  : null
              }
            />
            <InfoRow
              label="Expires"
              value={
                proposal?.expires_date
                  ? formatDate(proposal.expires_date as string)
                  : null
              }
            />
            <InfoRow
              label="Sent"
              value={
                proposal?.sent_at
                  ? formatDate(proposal.sent_at as string)
                  : null
              }
            />
            <InfoRow
              label="Sales Rep"
              value={str(proposal?.assigned_rep_name)}
            />
            <InfoRow
              label="Created"
              value={
                proposal?.created_at
                  ? formatDate(proposal.created_at as string)
                  : null
              }
            />
          </RailSection>

          <RailSection title="Activity">
            {id && (
              <EntityActivityPreview entityType="proposal" entityId={id} />
            )}
          </RailSection>
        </div>
      </div>
    </DetailPage>
  );
}
